
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CalendarPlus, CalendarClock, Users, Trash2, Edit, Calendar, Clock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const MentorOfficeHours = () => {
  const { isMentor, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Form state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [maxAttendees, setMaxAttendees] = useState(5);
  const [editId, setEditId] = useState<string | null>(null);
  
  // Check permissions
  useEffect(() => {
    if (!isMentor()) {
      navigate('/dashboard');
    }
  }, [isMentor, navigate]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      setTitle('');
      setDescription('');
      setStartDate('');
      setStartTime('');
      setEndTime('');
      setMaxAttendees(5);
      setEditId(null);
    }
  }, [isDialogOpen]);

  // Fetch mentor office hours
  const { data: officeHours, isLoading } = useQuery({
    queryKey: ['mentor-office-hours', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      try {
        const { data, error } = await supabase
          .from('office_hours')
          .select(`
            id,
            title,
            description,
            start_time,
            end_time,
            max_attendees,
            created_at,
            office_hours_attendees(id)
          `)
          .eq('mentor_id', user.id)
          .order('start_time', { ascending: true });
        
        if (error) throw error;
        
        return data.map(session => ({
          ...session,
          attendees_count: session.office_hours_attendees?.length || 0
        })) || [];
      } catch (error) {
        console.error('Error fetching office hours:', error);
        toast.error('Error loading office hours');
        return [];
      }
    },
    enabled: !!user
  });

  // Create office hours session
  const createOfficeHoursMutation = useMutation({
    mutationFn: async (sessionData: {
      title: string;
      description: string;
      start_time: string;
      end_time: string;
      max_attendees: number;
    }) => {
      if (!user) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .from('office_hours')
        .insert({
          mentor_id: user.id,
          ...sessionData
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentor-office-hours', user?.id] });
      toast.success('Office hours session created successfully');
      setIsDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error creating office hours:', error);
      toast.error('Failed to create office hours session');
    }
  });

  // Update office hours session
  const updateOfficeHoursMutation = useMutation({
    mutationFn: async ({
      id,
      sessionData
    }: {
      id: string;
      sessionData: {
        title: string;
        description: string;
        start_time: string;
        end_time: string;
        max_attendees: number;
      };
    }) => {
      const { data, error } = await supabase
        .from('office_hours')
        .update(sessionData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentor-office-hours', user?.id] });
      toast.success('Office hours session updated successfully');
      setIsDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error updating office hours:', error);
      toast.error('Failed to update office hours session');
    }
  });

  // Delete office hours session
  const deleteOfficeHoursMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('office_hours')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mentor-office-hours', user?.id] });
      toast.success('Office hours session deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting office hours:', error);
      toast.error('Failed to delete office hours session');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !startDate || !startTime || !endTime) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Combine date and time into ISO strings
    const startDateTime = `${startDate}T${startTime}:00`;
    const endDateTime = `${startDate}T${endTime}:00`;
    
    // Validate times
    if (new Date(endDateTime) <= new Date(startDateTime)) {
      toast.error('End time must be after start time');
      return;
    }
    
    const sessionData = {
      title,
      description: description || '',
      start_time: startDateTime,
      end_time: endDateTime,
      max_attendees: maxAttendees
    };
    
    if (editId) {
      updateOfficeHoursMutation.mutate({ id: editId, sessionData });
    } else {
      createOfficeHoursMutation.mutate(sessionData);
    }
  };

  const handleEdit = (session: any) => {
    const start = new Date(session.start_time);
    const end = new Date(session.end_time);
    
    setEditId(session.id);
    setTitle(session.title);
    setDescription(session.description || '');
    setStartDate(format(start, 'yyyy-MM-dd'));
    setStartTime(format(start, 'HH:mm'));
    setEndTime(format(end, 'HH:mm'));
    setMaxAttendees(session.max_attendees);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this office hours session?')) {
      deleteOfficeHoursMutation.mutate(id);
    }
  };

  const renderSessionsList = (sessions: any[]) => {
    if (sessions.length === 0) {
      return (
        <div className="text-center py-8">
          <CalendarClock className="h-12 w-12 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No office hours sessions found</h3>
          <p className="text-muted-foreground mt-1">
            {activeTab === 'upcoming' 
              ? 'You have no upcoming office hours scheduled.'
              : 'You have no past office hours sessions.'}
          </p>
          <Button 
            className="mt-4"
            onClick={() => setIsDialogOpen(true)}
          >
            <CalendarPlus className="h-4 w-4 mr-2" />
            Schedule New Session
          </Button>
        </div>
      );
    }
    
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {sessions.map(session => {
          const start = new Date(session.start_time);
          const end = new Date(session.end_time);
          const isPast = isBefore(end, new Date());
          const isFull = session.attendees_count >= session.max_attendees;
          
          return (
            <Card key={session.id} className="h-full">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl font-medium">{session.title}</CardTitle>
                  {!isPast && (
                    <div className="flex space-x-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(session)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-destructive"
                        onClick={() => handleDelete(session.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription>
                  <div className="flex items-center gap-1 mt-1">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{format(start, 'EEEE, MMMM d, yyyy')}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{format(start, 'h:mm a')} - {format(end, 'h:mm a')}</span>
                  </div>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  {session.description || 'No description provided.'}
                </p>
                <div className="flex items-center text-sm">
                  <Users className="h-4 w-4 mr-1 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {session.attendees_count} / {session.max_attendees} attendees
                  </span>
                </div>
              </CardContent>
              <CardFooter>
                {isPast ? (
                  <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300 font-medium">
                    Completed
                  </Badge>
                ) : isFull ? (
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 font-medium">
                    Full
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 font-medium">
                    Available
                  </Badge>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
    );
  };

  const now = new Date();
  const upcomingSessions = officeHours?.filter(session => isAfter(new Date(session.end_time), now)) || [];
  const pastSessions = officeHours?.filter(session => isBefore(new Date(session.end_time), now)) || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold section-header">Office Hours</h1>
          <p className="text-muted-foreground section-subheader">Manage your office hours sessions</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <CalendarPlus className="h-4 w-4 mr-2" />
          Schedule Session
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="upcoming">
            Upcoming
            {upcomingSessions.length > 0 && (
              <Badge className="ml-2 bg-primary/20 text-primary">{upcomingSessions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="past">
            Past Sessions
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="upcoming" className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : renderSessionsList(upcomingSessions)}
        </TabsContent>
        
        <TabsContent value="past" className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : renderSessionsList(pastSessions)}
        </TabsContent>
      </Tabs>
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Office Hours Session' : 'Schedule New Office Hours Session'}</DialogTitle>
            <DialogDescription>
              Set up an office hours session for students to attend.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Weekly Q&A Session"
                required
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">
                Description
              </label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What will you cover in this session?"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="startDate" className="text-sm font-medium">
                Date <span className="text-red-500">*</span>
              </label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="startTime" className="text-sm font-medium">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="endTime" className="text-sm font-medium">
                  End Time <span className="text-red-500">*</span>
                </label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="maxAttendees" className="text-sm font-medium">
                Maximum Attendees
              </label>
              <Input
                id="maxAttendees"
                type="number"
                min={1}
                max={50}
                value={maxAttendees}
                onChange={(e) => setMaxAttendees(parseInt(e.target.value) || 5)}
              />
            </div>
            
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
              <Button 
                type="submit"
                disabled={
                  createOfficeHoursMutation.isPending || 
                  updateOfficeHoursMutation.isPending
                }
              >
                {createOfficeHoursMutation.isPending || updateOfficeHoursMutation.isPending ? (
                  <>Saving...</>
                ) : editId ? (
                  <>Update Session</>
                ) : (
                  <>Schedule Session</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MentorOfficeHours;
